import React from 'react';
import { useDispatch, useSelector } from 'react-redux';
import { Zap, CreditCard, Loader2, Plug } from 'lucide-react';

import api from '../../../api/axios';
import { saveAgentSettings } from '../../../redux/DigestSlice/digestSlice';
import { linkedInLogin } from '../../../redux/LIAuthSlice/liAuthSlice';
import { budgetCoverage, googleConnectUrl } from './digestHelpers';

/**
 * The summary band above the triage queue: what today holds, what approving it
 * would cost, and whether the agent is even running on its own.
 *
 * Every number here is read from the live digest or the settings endpoint.
 * The design comp carried invented figures (a 24% reply rate, "last 7 days: 3")
 * that nothing in the API produces — those are rendered from real values or
 * shown as "—" rather than filled in with plausible-looking numbers.
 */

export const DDStat = ({ label, value, tint, sub }) => (
    <div className="cc-card" style={{ padding: '12px 16px', minWidth: 132 }}>
        <div className="cc-eyebrow">{label}</div>
        <div style={{ fontSize: 22, fontWeight: 800, letterSpacing: '-0.02em', marginTop: 4, color: tint || 'var(--cc-ink-900)' }}>
            {value}
        </div>
        {sub && <div className="cc-tiny" style={{ marginTop: 1 }}>{sub}</div>}
    </div>
);

export const DDSeg = ({ items, active, onChange }) => (
    <div className="cc-seg">
        {items.map((it) => (
            <button
                key={it.k}
                type="button"
                onClick={() => onChange && onChange(it.k)}
                className={it.k === active ? 'is-active' : ''}
            >
                {it.label}
                {it.n !== undefined && <span className="cc-seg-n">{it.n}</span>}
            </button>
        ))}
    </div>
);

const nf = (n) => Number(n || 0).toLocaleString();

export const CreditBar = ({ actions, approvedActions, approvedCredits, creditsBalance, onBuyCredits }) => {
    const balance = Number(creditsBalance) || 0;
    const { covered, total, totalCost } = budgetCoverage(actions, balance);
    const leftAfter = Math.max(0, balance - approvedCredits);
    const beyond = Math.max(0, totalCost - balance);

    // Widths are shares of everything today COULD cost, so the grey tail
    // honestly shows how much of the digest the balance cannot reach.
    const scale = Math.max(totalCost, balance, 1);
    const approvedPct = Math.min(100, (approvedCredits / scale) * 100);
    const remainingPct = Math.min(100 - approvedPct, (leftAfter / scale) * 100);

    const cell = (label, value, sub, tint) => (
        <div style={{ padding: '14px 18px', minWidth: 0 }}>
            <div className="cc-eyebrow">{label}</div>
            <div style={{ fontSize: 19, fontWeight: 800, letterSpacing: '-0.02em', marginTop: 5, color: tint || 'var(--cc-ink-900)' }}>{value}</div>
            <div className="cc-tiny" style={{ marginTop: 2 }}>{sub}</div>
        </div>
    );

    return (
        <div className="cc-card cc-credit-bar" style={{ padding: 0, overflow: 'hidden' }}>
            {cell('Balance', nf(balance), 'credits available')}
            <div style={{ borderLeft: '1px solid var(--cc-divider)' }}>
                {cell(
                    'Approved so far',
                    nf(approvedCredits),
                    `${approvedActions.length} item${approvedActions.length === 1 ? '' : 's'}`,
                    'var(--cc-primary)'
                )}
            </div>
            <div style={{ borderLeft: '1px solid var(--cc-divider)', padding: '14px 18px' }}>
                <div style={{ display: 'flex', alignItems: 'center', gap: 8 }}>
                    <div className="cc-eyebrow cc-grow">
                        {totalCost === 0
                            ? 'Nothing in today’s digest costs credits'
                            : covered >= total
                                ? `Budget covers all ${total} contact search${total === 1 ? '' : 'es'}`
                                : `Budget covers ${covered} of ${total} contact searches`}
                    </div>
                    <span className="cc-tiny" style={{ fontWeight: 600 }}>{nf(leftAfter)} left after this run</span>
                </div>
                <div style={{ marginTop: 9, height: 6, borderRadius: 9999, background: '#EDEFF4', overflow: 'hidden', display: 'flex' }}>
                    <span style={{ width: `${approvedPct}%`, background: 'var(--cc-primary)' }} />
                    <span style={{ width: `${remainingPct}%`, background: 'var(--cc-primary-100)' }} />
                </div>
                <div style={{ display: 'flex', gap: 16, marginTop: 8, flexWrap: 'wrap' }}>
                    <span className="cc-tiny" style={{ display: 'inline-flex', alignItems: 'center', gap: 5 }}>
                        <span style={{ width: 8, height: 8, borderRadius: 3, background: 'var(--cc-primary)' }} />Approved {nf(approvedCredits)}
                    </span>
                    <span className="cc-tiny" style={{ display: 'inline-flex', alignItems: 'center', gap: 5 }}>
                        <span style={{ width: 8, height: 8, borderRadius: 3, background: 'var(--cc-primary-100)' }} />Remaining balance {nf(leftAfter)}
                    </span>
                    {beyond > 0 && (
                        <span className="cc-tiny" style={{ display: 'inline-flex', alignItems: 'center', gap: 5 }}>
                            <span style={{ width: 8, height: 8, borderRadius: 3, background: '#EDEFF4' }} />Beyond budget {nf(beyond)}
                        </span>
                    )}
                </div>
            </div>
            <div style={{ padding: '14px 18px', borderLeft: '1px solid var(--cc-divider)' }}>
                <button type="button" className="cc-btn cc-btn-secondary cc-btn-sm" onClick={onBuyCredits}>
                    <CreditCard style={{ width: 12, height: 12 }} />Buy credits
                </button>
            </div>
        </div>
    );
};

/**
 * The agent's own state, stated before the queue rather than buried in a
 * settings drawer: whether it runs on a schedule, and what it is allowed to
 * send as. A user triaging 55 rows should know up front whether tomorrow's
 * digest will exist at all.
 */
export const SettingsStrip = ({ settings, channels, onOpenSettings }) => {
    const dispatch = useDispatch();
    const [saving, setSaving] = React.useState(false);
    const userEmail = useSelector((s) => s.auth.user?.email);
    const liConnecting = useSelector((s) => s.liauth?.isLoading);
    const enabled = Boolean(settings?.enabled);

    /**
     * A chip that reports a problem also OFFERS THE FIX.
     *
     * "Google not connected" was a dead label: it named the one thing standing
     * between the user and every send on the page, and gave them nowhere to go.
     * The connectable chips are now buttons.
     *
     * Google and Gmail run the SAME flow — gmail.send is a scope on the same
     * grant, not a second account — so both chips lead to one consent screen.
     * The URL comes from the shared helper, so this cannot drift from the
     * connection strip or the blocked send button, and it goes straight to
     * consent rather than bouncing through /profile.
     */
    const connectGoogle = () => {
        const url = googleConnectUrl({
            baseURL: api.defaults.baseURL || process.env.REACT_APP_API_BASE_URL || window.location.origin,
            userEmail,
            origin: window.location.origin
        });
        if (url) window.open(url, '_self');
    };

    // LinkedIn is a different shape: linkedInLogin fetches a Unipile auth URL
    // and Navbaar's global watcher opens the popup. Opening a window here would
    // race that watcher and produce two popups, so dispatching is the whole job.
    const connectLinkedIn = () => dispatch(linkedInLogin());

    const toggle = async () => {
        if (saving) return;
        setSaving(true);
        await dispatch(saveAgentSettings({ enabled: !enabled }));
        setSaving(false);
    };

    const gmail = channels?.gmail || {};
    const linkedin = channels?.linkedin || {};
    const google = channels?.google || {};

    const chips = [
        google.connected
            ? { label: 'Google connected', tone: 'cc-chip-success' }
            : { label: 'Google not connected', tone: 'cc-chip-danger', onClick: connectGoogle, cta: 'Connect' },
        !gmail.canSend
            ? { label: 'Gmail cannot send', tone: 'cc-chip-danger', onClick: connectGoogle, cta: 'Fix' }
            : gmail.assumed
                // Sending works; the permission simply was never recorded. A
                // successful send now records it, so this clears itself — no
                // reason to push anyone through consent again.
                ? { label: 'Gmail unconfirmed', tone: 'cc-chip-warn' }
                : { label: 'Gmail confirmed', tone: 'cc-chip-success' },
        linkedin.connected
            ? { label: 'LinkedIn connected', tone: 'cc-chip-success' }
            : {
                label: liConnecting ? 'Connecting LinkedIn…' : 'LinkedIn not connected',
                tone: 'cc-chip-danger',
                onClick: liConnecting ? null : connectLinkedIn,
                cta: 'Connect'
            },
    ];

    const caps = settings
        // fetchCap bounds contact-search rows, not the digest's size, so it is
        // named for what it limits.
        ? `caps ${settings.dailyCap ?? '—'} email / ${settings.linkedinCap ?? '—'} LinkedIn / ${settings.fetchCap ?? '—'} contact searches`
        : 'loading caps…';

    return (
        <div className="cc-card" style={{ padding: '12px 18px', display: 'flex', alignItems: 'center', gap: 14, flexWrap: 'wrap' }}>
            <div style={{ width: 32, height: 32, borderRadius: 9, background: 'var(--cc-primary-50)', color: 'var(--cc-primary)', display: 'grid', placeItems: 'center', flexShrink: 0 }}>
                <Zap style={{ width: 16, height: 16 }} />
            </div>
            <div className="cc-grow" style={{ minWidth: 200 }}>
                <div style={{ fontSize: 13, fontWeight: 700 }}>
                    {enabled
                        ? 'Morning runs are on — a digest is prepared for you daily'
                        : 'Morning runs are off — you’re generating digests by hand'}
                </div>
                <div className="cc-tiny" style={{ marginTop: 2 }}>
                    {enabled ? 'Prepared every day at 8:00 AM MT' : 'Turn on to prepare outreach every day at 8:00 AM MT'} · {caps}
                </div>
            </div>
            <div style={{ display: 'flex', gap: 6, flexWrap: 'wrap' }}>
                {chips.map((c) => (c.onClick ? (
                    <button
                        key={c.label}
                        type="button"
                        onClick={c.onClick}
                        className={`cc-chip ${c.tone}`}
                        title={`${c.cta} — opens the consent screen`}
                        style={{ cursor: 'pointer', border: '1px solid currentColor', font: 'inherit', fontSize: 11, fontWeight: 600 }}
                    >
                        <Plug style={{ width: 10, height: 10 }} />
                        {c.label}
                        {/* Green, against the chip's red. The chip states the
                            problem and the CTA is the way out, so it should not
                            read as more of the same alarm — #047857 is the same
                            green .cc-chip-success uses, and holds its contrast
                            on the chip's pale red ground. */}
                        <span style={{ textDecoration: 'underline', marginLeft: 2, color: '#047857', fontWeight: 700 }}>{c.cta}</span>
                    </button>
                ) : (
                    <span key={c.label} className={`cc-chip ${c.tone}`}>{c.label}</span>
                )))}
            </div>
            <button type="button" className="cc-btn cc-btn-secondary cc-btn-sm" onClick={onOpenSettings}>
                Agent settings
            </button>
            <button
                type="button"
                onClick={toggle}
                disabled={saving}
                aria-label={enabled ? 'Turn morning runs off' : 'Turn morning runs on'}
                aria-pressed={enabled}
                style={{
                    width: 40, height: 22, borderRadius: 9999, border: 0, padding: 0,
                    background: enabled ? 'var(--cc-primary)' : '#E2E5EC',
                    position: 'relative', flexShrink: 0, cursor: saving ? 'wait' : 'pointer',
                    transition: 'background .15s ease',
                }}
            >
                <span style={{
                    position: 'absolute', top: 3, left: enabled ? 21 : 3,
                    width: 16, height: 16, borderRadius: 9999, background: '#fff',
                    boxShadow: '0 1px 3px rgba(15,23,42,.25)', transition: 'left .15s ease',
                    display: 'grid', placeItems: 'center',
                }}>
                    {saving && <Loader2 style={{ width: 10, height: 10 }} className="animate-spin" />}
                </span>
            </button>
        </div>
    );
};
